import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaPlay, FaStop, FaCopy, FaChartLine, FaSort } from "react-icons/fa";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

export default function ViewExperiment() {
  const [experiments, setExperiments] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");
  const navigate = useNavigate();
  const userId = localStorage.getItem("user_id");

  useEffect(() => {
    if (!userId) {
      navigate("/");
      return;
    }
    fetchExperiments();
  }, [userId, navigate]);

  const fetchExperiments = async () => {
    const res = await fetch(`${BACKEND_URL}/api/experiments/user/${userId}`);
    const data = await res.json();

    const transformed = data.map((exp) => ({
      id: exp.id,
      testName: exp.testname,
      pageUrl: exp.pageurl,
      selector: exp.selector,
      goalSelector: exp.goalSelector,
      snippet: exp.snippet || "",
      status: (exp.status || "STOPPED").toUpperCase(),
      type: exp.type || "manual",
    }));

    // Sort by current sortOrder state
    const sorted = [...transformed].sort((a, b) => {
      return sortOrder === "asc" ? a.id - b.id : b.id - a.id;
    });

    setExperiments(sorted);
  };

  const handleSort = () => {
    const nextOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(nextOrder);
    const sorted = [...experiments].sort((a, b) => {
      return nextOrder === "asc" ? a.id - b.id : b.id - a.id;
    });
    setExperiments(sorted);
  };

  const goToEditor = (id) => navigate(`/editor/${id}`);
  
  const goToAnalytics = (id) => {
    window.location.href = `${BACKEND_URL}/analytics/${id}`;
  };

  const isEditorSetupComplete = (exp) => {
    if (exp.type !== "editor") return true;
    return Boolean(exp.goalSelector);
  };

  const handleCopy = (exp) => {
    if (exp.type === "editor" && !isEditorSetupComplete(exp)) {
      alert("Editor setup is not complete yet. Please complete setup in Editor before copying the snippet.");
      return;
    }

    if (!exp.snippet) {
      alert("No snippet available");
      return;
    }
    navigator.clipboard.writeText(exp.snippet);
    alert("Snippet copied!");
  };

  const toggleStartStop = async (exp) => {
    if (exp.status === "COMPLETED") return; // ✅ guard

    const nextStatus = exp.status === "STARTED" ? "STOPPED" : "STARTED";
    await fetch(`${BACKEND_URL}/api/experiments/${exp.id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: nextStatus }),
    });
    fetchExperiments();
  };

  // ✅ NEW: Complete handler
  const markComplete = async (id) => {
    await fetch(`${BACKEND_URL}/api/experiments/${id}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: "COMPLETED" }),
    });
    fetchExperiments();
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] text-slate-900 px-6 py-12 lg:px-10">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(67,179,126,0.08),_transparent_40%),radial-gradient(circle_at_top_right,_rgba(59,130,246,0.05),_transparent_35%)] pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto">
        <header className="flex items-center justify-between mb-8 gap-4">
          <div className="flex-1">
            <h2 className="text-4xl font-black text-slate-900 mb-2 tracking-tight">Your Experiments</h2>
            <p className="text-slate-600 text-sm md:text-base">Manage tests, start/stop experiments and view analytics.</p>
          </div>
          
          <div className="flex gap-3 shrink-0">
            <button 
              className="h-10 bg-emerald-600 hover:bg-emerald-700 border border-emerald-700 text-white px-4 rounded-lg font-semibold transition flex items-center gap-2 shadow-sm"
              onClick={handleSort}
              title={`Sort by ID: ${sortOrder === "asc" ? "Ascending" : "Descending"}`}
            >
              <FaSort /> ID {sortOrder === "asc" ? "↑" : "↓"}
            </button>
            <button 
              className="h-10 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-900 px-4 rounded-lg font-semibold transition"
              onClick={() => navigate('/dashboard')}
            >
              ← Back
            </button>
          </div>
        </header>

        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 overflow-x-auto">
          <table className="w-full border-collapse min-w-[1200px] table-fixed">
            <thead>
              <tr className="bg-emerald-50">
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Test ID</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Test Name</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Page URL</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Selector</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Goal Selector</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Type</th>
                <th className="px-4 py-3.5 text-center text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Start/Stop</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Complete</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Copy</th>
                <th className="px-4 py-3.5 text-left text-[11px] uppercase tracking-[0.08em] font-black text-emerald-900 border-b border-slate-200">Analytics</th>
              </tr>
            </thead>

            <tbody>
              {experiments.map((exp) => {
                const isCompleted = exp.status === "COMPLETED";

                return (
                  <tr key={exp.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors">
                    <td className="px-4 py-3.5 font-bold text-slate-400 font-mono text-sm">{exp.id}</td>
                    <td className="px-4 py-3.5 font-semibold text-slate-800 text-sm">{exp.testName}</td>
                    <td className="px-4 py-3.5">
                      <a 
                        href={exp.pageUrl} 
                        target="_blank" 
                        rel="noreferrer"
                        title={exp.pageUrl}
                        className="block max-w-[240px] truncate text-emerald-700 hover:text-emerald-800 font-medium underline underline-offset-2"
                      >
                        {exp.pageUrl}
                      </a>
                    </td>
                    <td
                      title={exp.selector || "-"}
                      className={`px-4 py-3.5 text-xs max-w-[180px] truncate ${
                        exp.selector ? "text-slate-700" : "text-slate-400"
                      }`}
                    >
                      {exp.selector || "-"}
                    </td>
                    <td
                      title={exp.goalSelector || "-"}
                      className={`px-4 py-3.5 text-xs max-w-[180px] truncate ${
                        exp.goalSelector ? "text-slate-700" : "text-slate-400"
                      }`}
                    >
                      {exp.goalSelector || "-"}
                    </td>
                    <td className="px-4 py-3.5">
                      {exp.type === "editor" ? (
                        <button
                          onClick={() => goToEditor(exp.id)}
                          className="text-emerald-700 hover:text-emerald-800 font-bold underline underline-offset-2"
                        >
                          Editor
                        </button>
                      ) : (
                        <span className="text-slate-700 font-medium">Manual</span>
                      )}
                    </td>

                    <td className="px-4 py-3.5 text-center">
                      <button
                        className="h-8 w-8 mx-auto rounded-lg border border-slate-200 bg-white text-base cursor-pointer flex items-center justify-center hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={isCompleted}
                        onClick={() => toggleStartStop(exp)}
                      >
                        {exp.status === "STARTED"
                          ? <FaStop className="text-red-500" />
                          : <FaPlay className="text-emerald-400" />
                        }
                      </button>
                    </td>

                    <td className="px-4 py-3.5">
                      <button
                        className={`px-3 py-1.5 rounded-full font-bold text-xs inline-block min-w-20 text-center transition ${
                          isCompleted
                            ? "bg-slate-200 text-slate-600 border border-slate-300 cursor-not-allowed"
                            : "bg-emerald-600 text-white border border-emerald-700 hover:bg-emerald-700"
                        }`}
                        disabled={isCompleted}
                        onClick={() => markComplete(exp.id)}
                      >
                        {isCompleted ? "Completed" : "Complete"}
                      </button>
                    </td>

                    <td className="px-4 py-3.5">
                      <button 
                        className="h-8 w-8 rounded-lg border border-slate-200 bg-white text-slate-700 hover:text-emerald-700 hover:bg-slate-50 hover:border-slate-300 cursor-pointer text-base transition flex items-center justify-center"
                        onClick={() => handleCopy(exp)}
                      >
                        <FaCopy />
                      </button>
                    </td>
                    <td className="px-4 py-3.5">
                      <button
                        title="Open Analytics"
                        aria-label="Open Analytics"
                        className="h-8 w-8 rounded-lg border border-blue-700 bg-blue-600 text-white hover:bg-blue-700 transition shadow-sm flex items-center justify-center"
                        onClick={() => goToAnalytics(exp.id)}
                      >
                        <FaChartLine />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

